import React from "react";

const CARDS = [
    {
        title: "Journey to Echo",
        subtitle: "Upcoming FPS Survival",
        text: "Bunker survival, vehicle repair, radio tower restoration and escape-focused progression.",
        number: "01",
    },
    {
        title: "City Delivery Simulator",
        subtitle: "Mobile Simulation",
        text: "Route-based delivery gameplay with upgrades, contracts and city-scale progression.",
        number: "02",
    },
    {
        title: "Traffic Car",
        subtitle: "Arcade Driving",
        text: "Fast traffic gameplay built around clean controls, quick reactions and short sessions.",
        number: "03",
    },
] as const;

const GamePreviewSection: React.FC = () => {
    return (
        <section id="about" className="relative overflow-hidden bg-[#080705] px-4 py-20 text-[#F3E8D6] sm:px-6 lg:px-10 lg:py-28">
            {/* BACKGROUND */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute left-1/2 top-[-260px] h-[520px] w-[900px] -translate-x-1/2 bg-[#B48B57]/14 blur-[120px]" />
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(232,221,202,0.12),transparent_34%)]" />

                <div
                    className="absolute inset-0 opacity-[0.045]"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, rgba(255,255,255,0.22) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.22) 1px, transparent 1px)",
                        backgroundSize: "44px 44px",
                    }}
                />

                {/* BIG ARC */}
                <div className="absolute left-1/2 top-[80px] h-[420px] w-[1050px] -translate-x-1/2 overflow-hidden">
                    <div className="absolute left-1/2 top-[120px] h-[620px] w-[1050px] -translate-x-1/2 rounded-t-full border-[34px] border-[#C6A06A]/35 blur-[2px]" />
                    <div className="absolute left-1/2 top-[120px] h-[620px] w-[1050px] -translate-x-1/2 rounded-t-full border-[2px] border-[#F3E8D6]/35" />
                    <div className="absolute left-1/2 top-[105px] h-[650px] w-[1100px] -translate-x-1/2 rounded-t-full border-[1px] border-[#C6A06A]/20" />
                </div>

                <div className="absolute inset-x-0 bottom-0 h-56 bg-gradient-to-t from-[#080705] to-transparent" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1280px]">
                {/* HEADER */}
                <div className="mx-auto max-w-[760px] text-center">
                    <p className="mb-5 text-[11px] font-black uppercase tracking-[0.26em] text-[#C6A06A]">
                        Northruins Projects
                    </p>

                    <h2 className="text-[40px] font-black uppercase leading-[0.95] tracking-[-0.045em] text-[#F6EAD8] sm:text-[58px] lg:text-[72px]">
                        Games with
                        <br />
                        <span className="text-[#B48B57]">atmosphere & feel.</span>
                    </h2>

                    <p className="mx-auto mt-5 max-w-[520px] text-[14px] leading-7 text-[#CDBFAE]/75 sm:text-[15px]">
                        Survival, simulation and arcade projects shaped around readable
                        systems, progression and strong visual identity.
                    </p>

                    <div className="mt-7 flex justify-center gap-3">
                        <button className="bg-[#F3E8D6] px-5 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-[#11100D] transition hover:-translate-y-1 hover:bg-white">
                            View Projects
                        </button>

                        <button className="border border-white/12 bg-white/[0.03] px-5 py-3 text-[11px] font-black uppercase tracking-[0.14em] text-[#F3E8D6] transition hover:-translate-y-1 hover:bg-white/[0.07]">
                            About Studio
                        </button>
                    </div>
                </div>

                {/* CARDS */}
                <div className="mt-16 grid gap-5 md:grid-cols-3">
                    {CARDS.map((card, index) => (
                        <article
                            key={card.title}
                            className="group relative min-h-[310px] overflow-hidden border border-[#302A21] bg-[#11100D]/85 p-6 shadow-[0_24px_70px_rgba(0,0,0,0.32)] transition duration-500 hover:-translate-y-2 hover:border-[#C6A06A]/70"
                            style={{
                                animation: `fadeUp 0.8s ease ${index * 0.12}s both`,
                            }}
                        >
                            <div className="pointer-events-none absolute inset-0 opacity-0 transition duration-500 group-hover:opacity-100">
                                <div className="absolute inset-x-0 bottom-0 h-40 bg-[radial-gradient(circle_at_bottom,rgba(198,160,106,0.20),transparent_55%)]" />
                            </div>

                            <div className="absolute right-5 top-5 text-[48px] font-black leading-none text-white/[0.035] transition duration-500 group-hover:text-[#C6A06A]/20">
                                {card.number}
                            </div>

                            <div className="relative z-10 flex h-full flex-col justify-between">
                                <div>
                                    <div className="mb-8 flex h-12 w-12 items-center justify-center border border-[#C6A06A]/35 bg-[#18140F] text-[12px] font-black text-[#D2B48C]">
                                        {card.number}
                                    </div>

                                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#C6A06A]">
                                        {card.subtitle}
                                    </p>

                                    <h3 className="mt-3 text-[26px] font-black uppercase leading-[0.95] tracking-[-0.03em] text-[#F3E8D6]">
                                        {card.title}
                                    </h3>

                                    <p className="mt-4 text-[13px] leading-7 text-[#CDBFAE]/75">
                                        {card.text}
                                    </p>
                                </div>

                                <div className="mt-8 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.16em] text-[#8F8270]">
                    Northruins
                  </span>

                                    <span className="h-px w-10 bg-[#C6A06A]/60 transition-all duration-500 group-hover:w-20" />
                                </div>
                            </div>

                            <div className="absolute bottom-0 left-0 h-[3px] w-0 bg-[#C6A06A] transition-all duration-500 group-hover:w-full" />
                        </article>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default React.memo(GamePreviewSection);